import React, { ReactNode, useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import {
  FaCamera,
  FaEye,
  FaEyeSlash,
  FaDownload,
  FaExternalLinkAlt,
  FaChevronLeft,
  FaPrint,
  FaLock,
  FaMoneyBillWave,
  FaPlay,
  FaEject,
  FaCrown,
  FaRocket
} from 'react-icons/fa'
import { LanguageSelector, Preview } from '@renderer/components'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { usePrintStore } from '@renderer/stores/printStore'
import { useFullscreenManager, useSessionLayout, type DeviceSettings } from '@renderer/hooks'
import cogoToast from '@successtar/cogo-toast'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { CameraGuideModal } from './CameraGuideModal'
import { PrinterGuideModal } from './PrinterGuideModal'
import { PrinterSettingsModal } from './PrinterSettingsModal'
import { RemotePrintSettingsModal } from './RemotePrintSettingsModal'
import { CameraPrompt } from './CameraPrompt'
import PinModal from '../PinModal'
import { Button } from '@renderer/components'
import { useParams } from 'react-router-dom'
import { useFirstLoadStore } from '@renderer/stores/firstLoadStore'
import CameraSettingsModal from './CameraSettingsModal'
import { useCreditsStore } from '@renderer/stores/creditsStore'
import { useConfigStore } from '@renderer/stores/configState'
import { UpgradeModal } from '../UpgradeModal'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { useOfflineStore } from '@renderer/stores/offlineStore'
import { useSubscriptionCache } from '@renderer/hooks/useSubscriptionCache'
import { useAuthStore } from '@renderer/stores/authStore'
import { useCameraStore } from '@renderer/stores/cameraStore'
import { useFullscreen } from '@renderer/utils'
import {
  capabilities,
  getHostAppVersion,
  getPreloadApi,
  hasElectronPreload,
  isInTauriShell,
  openExternalUrl,
} from '@renderer/lib/runtime'

interface SettingsModalProps {
  setShowSettings: (show: boolean, fromGear?: boolean) => void
  isFromGearButton?: boolean
  height: number
  devices?: DeviceSettings
  fetchDevices: () => Promise<void>
  cameraConnectionRef: React.RefObject<{
    id: string
    connected: boolean
    isPreviewAvailable: boolean
  }>
  isPreviewAvailable: boolean
  setIsPreviewAvailable: (available: boolean) => void
  localTempCamera: string
  setLocalTempCamera: (id: string) => void
}

export const SettingsModal: React.FC<SettingsModalProps> = ({
  setShowSettings,
  isFromGearButton = false,
  devices,
  fetchDevices,
  cameraConnectionRef,
  isPreviewAvailable,
  setIsPreviewAvailable,
  localTempCamera,
  setLocalTempCamera
}) => {
  const { t } = useTranslation()
  const { config } = useConfigStore()
  const { primaryColor } = config?.theme || {
    primaryColor: '#3B82F6'
  }

  // Configuration: Set to false to disable app update notifications
  const ENABLE_UPDATE_NOTIFICATIONS = false
  const [showPassword, setShowPassword] = useState(false)
  const [showGuideModal, setShowGuideModal] = useState(false)
  const [showPrinterGuideModal, setShowPrinterGuideModal] = useState(false)
  const [showPrinterSettings, setShowPrinterSettings] = useState(false)
  const [showRemotePrintSettings, setShowRemotePrintSettings] = useState(false)
  const [showChangePinModal, setShowChangePinModal] = useState(false)
  const [showCameraSettings, setShowCameraSettings] = useState(false)
  const [downloadDestination, setDownloadDestination] = useState<string | undefined>()
  const { projectId } = useParams()
  const { getPageSettings } = useConfigStore()
  const welcomeSettings = getPageSettings('welcome')
  const customPath = welcomeSettings?.customPath
  const isEditCustomPath = customPath === 'edit'
  const {
    selectedPrinter,
    password,
    setPassword,
    downloadLocal,
    setDownloadLocal,
    cameraSettings,
    autoStart,
    setAutoStart,
    autoLaunch,
    setAutoLaunch,
    setDefaultProjectId,
    setSelectedCamera,
    selectedCamera,
    debugFps,
    setDebugFps,
    deviceId,
    enableScreensaver,
    screensaverTimeout,
    setSetting,
    suppressBrowserGestures,
    setSuppressBrowserGestures
  } = useSettingsStore()
  const { setSessionState, userId, projectId: sessionProjectId } = useSessionStateStore()
  const { user: authUser } = useAuthStore()
  const {
    setSelectedPrinter,
    isPrintingEnabled,
    togglePrinting,
    setRemotePrinters,
    remotePrinters,
    selectedPrinterTarget,
    selectedRemotePrinterMachineId,
    selectRemotePrinter,
    selectDefaultPrinter
  } = usePrintStore()
  const { setIsFirstLoad } = useFirstLoadStore()
  const { credits, fetchSubscriptionAndCredits } = useCreditsStore()
  const { activationCode } = useSettingsStore()
  const { type: packageType, isValid, proTrialExpiresAt } = useSubscriptionStore()
  const hasDeviceSubscription = Boolean(activationCode && isValid)
  const displayType = hasDeviceSubscription ? packageType : 'free'
  const [remainingTime, setRemainingTime] = useState<string | null>(null)
  const isKlakKlikKluk = window.location.hostname.includes('klakklikkluk')
  const { appVersion, setAppVersion } = useOfflineStore()
  const { toggleFullscreen } = useFullscreen()

  // Initialize subscription cache
  useSubscriptionCache()

  const { isFullscreen } = useFullscreenManager()
  const { isCompact: isViewportCompact, isPortraitViewport } = useSessionLayout()
  const useCompactModal = capabilities.isMobile || isViewportCompact
  /** Physical phone portrait: smaller type + aligned padding with the rest of the modal */
  const settingsPortraitCompact = useCompactModal && isPortraitViewport

  const [localTempPrinter, setLocalTempPrinter] = useState<string>('')
  const [pinModalTitle, setPinModalTitle] = useState<string>('Change PIN')
  const [pinModalSubtitle, setPinModalSubtitle] = useState<string | ReactNode>(
    'Enter 6-digit PIN to continue'
  )

  const [showCameraPrompt, setShowCameraPrompt] = useState(false)
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [pendingCameraConnection, setPendingCameraConnection] = useState<{
    type: 'detect' | 'usb'
    params: {
      productId?: number
      vendorId?: number
    }
  } | null>(null)

  const [projectTitle, setProjectTitle] = useState<string>('')

  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  const [localAppVersion, setLocalAppVersion] = useState<string | undefined>(undefined)
  const [latestAppVersion, setLatestAppVersion] = useState<string | undefined>(undefined)
  const [showUpdateBubble, setShowUpdateBubble] = useState<boolean>(false)
  const [hasConnectionError, setHasConnectionError] = useState<boolean>(false)

  // Privilege restrictions state (simplified - only detects Store installation)
  const [privilegeRestrictions, setPrivilegeRestrictions] = useState<{
    isStoreInstall: boolean
  } | null>(null)

  // Windows system controls state
  const [sysControlsStatus, setSysControlsStatus] = useState<{
    edgeSwipe: string
    lockOnWake: { AC: string; DC: string }
    timeouts: {
      VIDEO_AC: string
      VIDEO_DC: string
      SLEEP_AC: string
      SLEEP_DC: string
      HAS_BATTERY: 'True' | 'False'
    }
  } | null>(null)
  const [sysControlsLoading, setSysControlsLoading] = useState<{
    edgeSwipe: boolean
    lockOnWakeDisable: boolean
    lockOnWakeEnable: boolean
    timeouts: boolean
  }>({
    edgeSwipe: false,
    lockOnWakeDisable: false,
    lockOnWakeEnable: false,
    timeouts: false
  })

  // Common toggle switch styles
  const toggleSwitchClasses = `w-11 h-6 bg-gray-200 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all`

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return (): void => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Fetch devices every 5 seconds, but stop when unmounted
  useEffect(() => {
    setDefaultProjectId(projectId)

    // Trigger log upload silently in background when settings modal is opened
    const api = getPreloadApi()
    if (api?.logs?.uploadNow) {
      api.logs.uploadNow().catch((err) => console.log('Silent log upload skipped/failed:', err))
    }
  }, [])

  // ProTrial countdown logic (only when device subscription is active)
  useEffect(() => {
    let timer: NodeJS.Timeout | null = null
    if (hasDeviceSubscription && displayType?.toLowerCase() === 'protrial' && proTrialExpiresAt) {
      const expiryDate = new Date(proTrialExpiresAt)

      const updateCountdown = (): void => {
        const now = new Date()
        const diff = expiryDate.getTime() - now.getTime()

        if (diff <= 0) {
          setRemainingTime(t('proTrial.expired'))
          if (timer) clearInterval(timer)
          return
        }

        const days = Math.floor(diff / (1000 * 60 * 60 * 24))
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
        const seconds = Math.floor((diff % (1000 * 60)) / 1000)

        if (days === 0) {
          setRemainingTime(t('proTrial.timeRemainingRealtime', { hours, minutes, seconds }))
        } else {
          setRemainingTime(t('proTrial.timeRemainingDays', { days }))
        }
      }

      updateCountdown()
      timer = setInterval(updateCountdown, 1000)
    } else {
      setRemainingTime(null)
    }

    return (): void => {
      if (timer) clearInterval(timer)
    }
  }, [hasDeviceSubscription, displayType, proTrialExpiresAt, t])

  useEffect(() => {
    setTimeout(() => {
      if (selectedCamera) {
        handleCameraChange(selectedCamera)
      }
      if (selectedPrinter && devices?.printers && !devices.printers.includes(selectedPrinter)) {
        setSelectedPrinter(undefined)
        setLocalTempPrinter('')
      } else if (selectedPrinter) {
        setLocalTempPrinter(selectedPrinter)
      }
    }, 1000)
  }, [devices, isFromGearButton])

  useEffect(() => {
    if (selectedPrinter) {
      setLocalTempPrinter(selectedPrinter)
    }
  }, [selectedPrinter])

  // Add this effect to fetch project details and credits
  // Discover remote printers (any running booth registered for this user) and feed the dropdown.
  useEffect(() => {
    const fetchRemotePrinters = async (): Promise<void> => {
      try {
        const response = await fetch(`${process.env.VITE_API_URL}/api/remote-printer`, {
          credentials: 'include',
          cache: 'no-cache'
        })
        if (!response.ok) return
        const data = await response.json()
        const printers = Array.isArray(data?.printers) ? data.printers : []
        setRemotePrinters(
          printers
            .filter((p) => p?.tunnelUrl)
            .map((p) => ({
              machineIdentifier: String(p._id),
              tunnelUrl: p.tunnelUrl,
              printerName: p.printerName || 'DS-RX1',
              projectName: p.projectName || undefined,
              printSettings: p.printSettings,
              isAvailable: p.isAvailable ?? true
            }))
        )
      } catch (error) {
        console.error('Error fetching remote printers:', error)
      }
    }
    fetchRemotePrinters()
  }, [setRemotePrinters])

  useEffect(() => {
    const fetchProjectDetails = async (): Promise<void> => {
      if (!projectId) return

      try {
        const response = await fetch(`${process.env.VITE_API_URL}/api/projects/${projectId}`, {
          credentials: 'include'
        })
        const data = await response.json()
        setProjectTitle(data.title)
      } catch (error) {
        console.error('Error fetching project details:', error)
      }
    }

    fetchProjectDetails()
    if (!isKlakKlikKluk) {
      fetchSubscriptionAndCredits()
    }

    const fetchAppVersion = async (): Promise<void> => {
      let version: string | null | undefined
      const v = await getHostAppVersion()
      if (v) {
        version = v
        setLocalAppVersion(version)
        setAppVersion(version)
      }

      try {
        const response = await fetch(`${process.env.VITE_API_URL}/api/releases`)
        if (response.ok) {
          const data = await response.json()
          const latestVersion = data[0].version.replace('v', '')
          setLatestAppVersion(latestVersion)

          if (version && latestVersion) {
            const currentParts = version.split('.').map(Number)
            const latestParts = latestVersion.split('.').map(Number)

            let needsUpdate = false
            for (let i = 0; i < Math.max(currentParts.length, latestParts.length); i++) {
              const current = currentParts[i] || 0
              const latest = latestParts[i] || 0

              if (latest > current) {
                needsUpdate = true
                break
              } else if (latest < current) {
                needsUpdate = false
                break
              }
            }
            setShowUpdateBubble(needsUpdate)
          } else if (latestVersion) {
            setShowUpdateBubble(true)
          }
        }
      } catch (error) {
        console.error('Error fetching latest release:', error)
      }
    }
    fetchAppVersion()

    const fetchDownloadPath = async (): Promise<void> => {
      const dlApi = getPreloadApi()
      if (
        hasElectronPreload() &&
        dlApi &&
        'getDownloadDestination' in dlApi &&
        typeof dlApi.getDownloadDestination === 'function'
      ) {
        try {
          const dest = await dlApi.getDownloadDestination()
          setDownloadDestination(dest || undefined)
        } catch (error) {
          console.error('Failed to get download destination:', error)
        }
      }
    }
    fetchDownloadPath()
  }, [projectId])

  const handleCameraChange = async (
    newCameraId: string,
    event?: MouseEvent | TouchEvent | Event
  ): Promise<void> => {
    const isClick = event?.isTrusted || isFromGearButton // Consider gear button click as trusted
    try {
      if (newCameraId === 'detect') {
        if (!isClick) {
          setShowCameraPrompt(true)
          setPendingCameraConnection({
            type: 'detect',
            params: {}
          })
          return
        }

        const result = await unifiedCameraUtils.tryToConnect('camera', {}, 'primary')
        const healthCheck = await unifiedCameraUtils.isDeviceResponding('primary')
        if (!healthCheck.isResponding) {
          await unifiedCameraUtils.disconnect('primary')
          throw new Error('Camera connected but not responding')
        }
        setIsPreviewAvailable(true)
        setLocalTempCamera(newCameraId)

        const deviceName = result.deviceInfo
          ? `${result.deviceInfo.manufacturerName || ''} ${result.deviceInfo.productName || ''}`.trim() ||
          'USB Camera'
          : 'USB Camera'

        setSessionState({
          devices: {
            camera: {
              id: newCameraId,
              name: deviceName,
              status: 'ready',
              type: 'camera'
            }
          }
        })

        setSelectedCamera(newCameraId)

        // Increment connection key to force Preview remount
        const { incrementCameraConnectionKey } = useSettingsStore.getState()
        incrementCameraConnectionKey()

        if (cameraConnectionRef?.current) {
          Object.assign(cameraConnectionRef.current, {
            id: newCameraId,
            connected: true,
            isPreviewAvailable: true
          })
        }
        return
      }
    } catch (err) {
      const errorMessage = (err as Error).message
      cogoToast.error('Failed to detect camera')
      cogoToast.error(errorMessage)
      setHasConnectionError(true)
      setIsFirstLoad(false)
      setIsPreviewAvailable(false)
      setLocalTempCamera('')
    }

    setLocalTempCamera(newCameraId)
    setIsPreviewAvailable(false)

    if (cameraConnectionRef?.current) {
      Object.assign(cameraConnectionRef.current, {
        id: '',
        connected: false,
        isPreviewAvailable: false
      })
    }

    if (!newCameraId) {
      return
    }

    try {
      // Find the camera device from either webcams or USB cameras
      const webcam = devices?.cameras.webcams.find((w) => w.deviceId === newCameraId)
      const usbCamera = devices?.cameras.usbCameras.find((c) => `${c.productId}` === newCameraId)

      if (usbCamera) {
        if (!usbCamera.hasDriver) {
          setShowGuideModal(true)
          return
        }

        if (!isClick) {
          setShowCameraPrompt(true)
          setPendingCameraConnection({
            type: 'usb',
            params: {
              productId: usbCamera.productId,
              vendorId: usbCamera.vendorId
            }
          })
          return
        }

        await unifiedCameraUtils.tryToConnect(
          'camera',
          {
            productId: usbCamera.productId,
            vendorId: usbCamera.vendorId
          },
          'primary'
        )
        const healthCheckUsb = await unifiedCameraUtils.isDeviceResponding('primary')
        if (!healthCheckUsb.isResponding) {
          await unifiedCameraUtils.disconnect('primary')
          throw new Error('Camera connected but not responding')
        }
        setIsPreviewAvailable(true)
        setLocalTempCamera(newCameraId)
        setSessionState({
          devices: {
            camera: {
              id: newCameraId,
              name: usbCamera.brand || 'USB Camera',
              status: 'ready',
              type: 'camera'
            }
          }
        })

        setSelectedCamera(newCameraId)

        // Increment connection key to force Preview remount
        const { incrementCameraConnectionKey } = useSettingsStore.getState()
        incrementCameraConnectionKey()

        if (cameraConnectionRef?.current) {
          Object.assign(cameraConnectionRef.current, {
            id: newCameraId,
            connected: true,
            isPreviewAvailable: true
          })
        }
      } else if (webcam) {
        await unifiedCameraUtils.tryToConnect('webcam', newCameraId, 'primary')
        const healthCheckWebcam = await unifiedCameraUtils.isDeviceResponding('primary')
        if (!healthCheckWebcam.isResponding) {
          await unifiedCameraUtils.disconnect('primary')
          throw new Error('Camera connected but not responding')
        }
        setIsPreviewAvailable(true)
        setLocalTempCamera(newCameraId)
        setSessionState({
          devices: {
            camera: {
              id: newCameraId,
              name: webcam.label || 'Webcam',
              status: 'ready',
              type: 'webcam'
            }
          }
        })

        setSelectedCamera(newCameraId)

        // Increment connection key to force Preview remount
        const { incrementCameraConnectionKey } = useSettingsStore.getState()
        incrementCameraConnectionKey()

        if (cameraConnectionRef?.current) {
          Object.assign(cameraConnectionRef.current, {
            id: newCameraId,
            connected: true,
            isPreviewAvailable: true
          })
        }
      }
    } catch (err) {
      const errorMessage = (err as Error).message
      await unifiedCameraUtils.disconnect()
      cogoToast.error(errorMessage || 'Failed to change camera')
      setHasConnectionError(true)
      setIsFirstLoad(false)
      setIsPreviewAvailable(false)
      setLocalTempCamera('')
    }
  }

  const handleCameraPromptStart = async (): Promise<void> => {
    if (!pendingCameraConnection) return

    try {
      if (pendingCameraConnection.type === 'detect') {
        const result = await unifiedCameraUtils.tryToConnect('camera', {}, 'primary')
        const healthCheckDetect = await unifiedCameraUtils.isDeviceResponding('primary')
        if (!healthCheckDetect.isResponding) {
          await unifiedCameraUtils.disconnect('primary')
          throw new Error('Camera connected but not responding')
        }
        setIsPreviewAvailable(true)
        setLocalTempCamera('detect')

        const deviceName = result.deviceInfo
          ? `${result.deviceInfo.manufacturerName || ''} ${result.deviceInfo.productName || ''}`.trim() ||
          'USB Camera'
          : 'USB Camera'

        setSessionState({
          devices: {
            camera: {
              id: 'detect',
              name: deviceName,
              status: 'ready',
              type: 'camera'
            }
          }
        })

        setSelectedCamera('detect')

        // Increment connection key to force Preview remount
        const { incrementCameraConnectionKey } = useSettingsStore.getState()
        incrementCameraConnectionKey()

        if (cameraConnectionRef?.current) {
          Object.assign(cameraConnectionRef.current, {
            id: 'detect',
            connected: true,
            isPreviewAvailable: true
          })
        }
      } else {
        await unifiedCameraUtils.tryToConnect('camera', pendingCameraConnection.params, 'primary')
        const healthCheckUsbPrompt = await unifiedCameraUtils.isDeviceResponding('primary')
        if (!healthCheckUsbPrompt.isResponding) {
          await unifiedCameraUtils.disconnect('primary')
          throw new Error('Camera connected but not responding')
        }
        setIsPreviewAvailable(true)
        const newCameraId = `${pendingCameraConnection.params.productId}`
        setLocalTempCamera(newCameraId)
        setSessionState({
          devices: {
            camera: {
              id: newCameraId,
              name: 'USB Camera',
              status: 'ready',
              type: 'camera'
            }
          }
        })

        setSelectedCamera(newCameraId)

        // Increment connection key to force Preview remount
        const { incrementCameraConnectionKey } = useSettingsStore.getState()
        incrementCameraConnectionKey()

        if (cameraConnectionRef?.current) {
          Object.assign(cameraConnectionRef.current, {
            id: newCameraId,
            connected: true,
            isPreviewAvailable: true
          })
        }
      }
    } catch (err) {
      const errorMessage = (err as Error).message
      setIsFirstLoad(false)
      cogoToast.error('Failed to start camera')
      cogoToast.error(errorMessage)
      setHasConnectionError(true)
      setIsPreviewAvailable(false)
      setLocalTempCamera('')
    } finally {
      setShowCameraPrompt(false)
      setPendingCameraConnection(null)
    }
  }

  // Unified printer picker: "Default (System Dialog)", a local system printer (Electron only),
  // or a remote booth. Values are namespaced ("local:<name>" / "remote:<machineId>") so one
  // <select> can drive all three targets.
  const handleUnifiedPrinterChange = async (
    e: React.ChangeEvent<HTMLSelectElement>
  ): Promise<void> => {
    const raw = e.target.value
    if (raw.startsWith('remote:')) {
      selectRemotePrinter(raw.slice('remote:'.length))
      setSelectedPrinter(undefined)
      setLocalTempPrinter('')
      setSessionState({ devices: { printer: null } })
    } else if (raw.startsWith('local:')) {
      const name = raw.slice('local:'.length)
      selectDefaultPrinter()
      setLocalTempPrinter(name)
      setSelectedPrinter(name)
      setSessionState({
        devices: { printer: { id: name, name, status: 'ready' } }
      })
    } else {
      selectDefaultPrinter()
      setSelectedPrinter(undefined)
      setLocalTempPrinter('')
      setSessionState({ devices: { printer: null } })
    }
  }

  const handlePinChange = (newPin: string): void => {
    setPassword(newPin)
    setShowChangePinModal(false)
    cogoToast.success(t('settings.feedback.pinUpdated'))
  }

  const handleChangePinClick = (): void => {
    setPinModalTitle(t('settings.pinModal.change.title'))
    setPinModalSubtitle(
      <div>
        <p>{t('settings.pinModal.change.subtitle')}</p>
        <ul className="list-disc pl-16 py-2">
          <li className="flex items-center">
            <FaLock className="mr-1" /> {t('settings.pinModal.usage.unlock')}
          </li>
          <li className="flex items-center">
            <FaMoneyBillWave className="mr-1" /> {t('settings.pinModal.usage.skip')}
          </li>
        </ul>
      </div>
    )
    setShowChangePinModal(true)
  }

  const handleSetPinClick = (): void => {
    setPinModalTitle(t('settings.pinModal.set.title'))
    setPinModalSubtitle(
      <div>
        <p>{t('settings.pinModal.set.subtitle')}</p>
        <ul className="list-disc pl-16 py-2">
          <li className="flex items-center">
            <FaLock className="mr-1" /> {t('settings.pinModal.usage.unlock')}
          </li>
          <li className="flex items-center">
            <FaMoneyBillWave className="mr-1" /> {t('settings.pinModal.usage.skip')}
          </li>
        </ul>
      </div>
    )
    setShowChangePinModal(true)
  }

  const handleContinue = async (): Promise<void> => {
    // Skip camera requirement for edit customPath
    if (!isEditCustomPath && !isPreviewAvailable) {
      cogoToast.info(t('settings.feedback.selectCamera'))
      setSelectedCamera(undefined)
      return
    }
    if (!password || password.length !== 6) {
      handleSetPinClick()
      return
    }
    // If printing is enabled and we're in Electron, user must select a printer before continuing
    // In web browser, printer selection is not available so we allow continuing without one
    if (hasElectronPreload() && isPrintingEnabled && !selectedPrinter) {
      if (isFullscreen) {
        try {
          await toggleFullscreen()
        } catch (error) {
          console.warn('[SETTINGS] Failed to exit fullscreen after missing printer selection:', error)
        }
      }
      cogoToast.info(t('settings.feedback.selectPrinter', 'Please select a printer to continue'))
      return
    }

    // Helper function to check PIN strength
    const checkPinStrength = (pin: string): 'weak' | 'strong' => {
      if (!pin || pin.length !== 6) return 'strong' // Not a valid PIN, don't flag as weak
      // Check if all digits are the same
      const firstDigit = pin[0]
      const allSame = pin.split('').every((digit) => digit === firstDigit)
      return allSame ? 'weak' : 'strong'
    }

    // Send telemetry (non-blocking)
    const sendTelemetry = async (): Promise<void> => {
      try {
        // Use email as userId (backend expects email as userId)
        const userEmail = authUser?.email || userId || ''
        if (!deviceId || !userEmail) {
          console.warn('[SETTINGS] Missing required fields for telemetry:', {
            hasDeviceId: !!deviceId,
            hasUserEmail: !!userEmail
          })
          return
        }

        // Try to get GPS location first (more accurate), fallback to IP if fails
        let latitude: number | undefined = undefined
        let longitude: number | undefined = undefined

        if (typeof navigator !== 'undefined' && navigator.geolocation) {
          try {
            const isElectron = hasElectronPreload()
            const timeoutMs = isElectron ? 10000 : 5000

            const position = await new Promise<GeolocationPosition>((resolve, reject) => {
              const timeoutId = setTimeout(() => {
                reject(new Error('Geolocation timeout'))
              }, timeoutMs)

              navigator.geolocation.getCurrentPosition(
                (pos) => {
                  clearTimeout(timeoutId)
                  resolve(pos)
                },
                (err) => {
                  clearTimeout(timeoutId)
                  reject(err)
                },
                {
                  enableHighAccuracy: false, // Use network-based location (faster, less battery)
                  timeout: timeoutMs,
                  maximumAge: isElectron ? 300000 : 60000 // Accept cached position up to 5 minutes old in Electron
                }
              )
            })

            latitude = position.coords.latitude
            longitude = position.coords.longitude
            console.log('[SETTINGS] [LOCATION] ✅ GPS location captured:', { latitude, longitude })
          } catch (error) {
            console.log('[SETTINGS] [LOCATION] ⚠️ GPS failed:', error)
          }
        } else {
          console.log('[SETTINGS] [LOCATION] Geolocation API not available, will use IP fallback')
        }

        // Collect structured telemetry data from all relevant stores
        const settingsStore = useSettingsStore.getState()
        const subscriptionStore = useSubscriptionStore.getState()
        const creditsStore = useCreditsStore.getState()
        const printStore = usePrintStore.getState()
        const sessionStore = useSessionStateStore.getState()
        const cameraStore = useCameraStore.getState()

        const telemetry = {
          userId: userEmail,
          deviceId: deviceId,
          appVersion: appVersion || 'unknown',
          platform: hasElectronPreload() ? 'electron' : isInTauriShell() ? 'tauri' : 'web',
          subscription: {
            package: creditsStore.package || subscriptionStore.type || 'free',
            credits: creditsStore.credits,
            isValid: subscriptionStore.isValid || false,
            token: settingsStore.activationCode || undefined
          },
          features: {
            ejectCameraWhenIdle: settingsStore.ejectCameraWhenIdle ?? false,
            autoStart: settingsStore.autoStart ?? false,
            autoLaunch: settingsStore.autoLaunch ?? false,
            downloadLocal: settingsStore.downloadLocal ?? false,
            isPrintingEnabled: printStore.isPrintingEnabled ?? false
          },
          camera: {
            selectedCamera: settingsStore.selectedCamera || null,
            deviceType: cameraStore.deviceType || null,
            useShutterCapture: settingsStore.cameraSettings?.useShutterCapture ?? false,
            captureQuality: {
              maxWidth: settingsStore.cameraSettings?.captureQuality?.maxWidth || 1920,
              maxHeight: settingsStore.cameraSettings?.captureQuality?.maxHeight || 1080,
              quality: settingsStore.cameraSettings?.captureQuality?.quality || 1.0
            },
            previewQuality: settingsStore.cameraSettings?.previewQuality
              ? {
                maxWidth: settingsStore.cameraSettings.previewQuality.maxWidth || 1280,
                maxHeight: settingsStore.cameraSettings.previewQuality.maxHeight || 720,
                quality: settingsStore.cameraSettings.previewQuality.quality || 0.8,
                resizeQuality:
                  settingsStore.cameraSettings.previewQuality.resizeQuality || 'medium'
              }
              : {
                maxWidth: 1280,
                maxHeight: 720,
                quality: 0.8,
                resizeQuality: 'medium'
              },
            captureRatio:
              settingsStore.cameraSettings?.captureRatio === 'auto'
                ? 'auto'
                : typeof settingsStore.cameraSettings?.captureRatio === 'number' &&
                  Number.isFinite(settingsStore.cameraSettings.captureRatio)
                  ? settingsStore.cameraSettings.captureRatio
                  : undefined,
            mirror: settingsStore.cameraSettings?.mirror ?? false,
            applyFilterOnPreview: settingsStore.cameraSettings?.applyFilterOnPreview ?? false,
            previewRotation: settingsStore.cameraSettings?.previewRotation ?? 0,
            captureRotation: settingsStore.cameraSettings?.captureRotation ?? 0,
            selectedSecondaryCamera: settingsStore.selectedSecondaryCamera || null,
            secondaryCameraSettings: settingsStore.secondaryCameraSettings
              ? {
                captureRatio:
                  settingsStore.secondaryCameraSettings.captureRatio === 'auto'
                    ? 'auto'
                    : typeof settingsStore.secondaryCameraSettings.captureRatio === 'number' &&
                      Number.isFinite(settingsStore.secondaryCameraSettings.captureRatio)
                      ? settingsStore.secondaryCameraSettings.captureRatio
                      : undefined,
                mirror: settingsStore.secondaryCameraSettings.mirror ?? false,
                useShutterCapture:
                  settingsStore.secondaryCameraSettings.useShutterCapture ?? false,
                applyFilterOnPreview:
                  settingsStore.secondaryCameraSettings.applyFilterOnPreview ?? false,
                previewQuality: settingsStore.secondaryCameraSettings.previewQuality
                  ? {
                    maxWidth:
                      settingsStore.secondaryCameraSettings.previewQuality.maxWidth || 1280,
                    maxHeight:
                      settingsStore.secondaryCameraSettings.previewQuality.maxHeight || 720,
                    quality:
                      settingsStore.secondaryCameraSettings.previewQuality.quality || 0.8,
                    resizeQuality:
                      settingsStore.secondaryCameraSettings.previewQuality.resizeQuality ||
                      'medium'
                  }
                  : undefined,
                captureQuality: settingsStore.secondaryCameraSettings.captureQuality
                  ? {
                    maxWidth:
                      settingsStore.secondaryCameraSettings.captureQuality.maxWidth || 1920,
                    maxHeight:
                      settingsStore.secondaryCameraSettings.captureQuality.maxHeight || 1080,
                    quality:
                      settingsStore.secondaryCameraSettings.captureQuality.quality || 1.0,
                    resizeQuality:
                      settingsStore.secondaryCameraSettings.captureQuality.resizeQuality ||
                      'high'
                  }
                  : undefined,
                previewRotation: settingsStore.secondaryCameraSettings.previewRotation ?? 0,
                captureRotation: settingsStore.secondaryCameraSettings.captureRotation ?? 0
              }
              : undefined
          },
          printer: {
            selectedPrinter: printStore.selectedPrinter || settingsStore.selectedPrinter || null,
            useCSharpPrinting: printStore.useCSharpPrinting ?? false,
            useSumatraPDF: printStore.useSumatraPDF ?? false,
            settings: {
              orientation: printStore.settings.orientation || 'landscape',
              pageSize: {
                height: printStore.settings.pageSize?.height || 105000,
                width: printStore.settings.pageSize?.width || 156100
              },
              margins: printStore.settings.margins || undefined,
              dpi: printStore.settings.dpi || undefined,
              scaleFactor: printStore.settings.scaleFactor || undefined,
              printBackground: printStore.settings.printBackground || undefined,
              printResolutionScale: printStore.settings.printResolutionScale || undefined
            },
            secondaryPrinter: printStore.secondaryPrinter
              ? {
                printerName: printStore.secondaryPrinter.printerName,
                useSumatraPDF: printStore.secondaryUseSumatraPDF ?? false,
                purpose: printStore.secondaryPrinter.purpose || undefined,
                settings: {
                  orientation: printStore.secondaryPrinter.settings.orientation || 'landscape',
                  pageSize: {
                    height: printStore.secondaryPrinter.settings.pageSize?.height || 105000,
                    width: printStore.secondaryPrinter.settings.pageSize?.width || 156100
                  },
                  margins: printStore.secondaryPrinter.settings.margins || undefined,
                  dpi: printStore.secondaryPrinter.settings.dpi || undefined,
                  scaleFactor: printStore.secondaryPrinter.settings.scaleFactor || undefined,
                  printBackground:
                    printStore.secondaryPrinter.settings.printBackground || undefined,
                  printResolutionScale:
                    printStore.secondaryPrinter.settings.printResolutionScale || undefined
                },
                frameIds: printStore.secondaryPrinter.frameIds || []
              }
              : undefined,
            tertiaryPrinter: printStore.tertiaryPrinter
              ? {
                printerName: printStore.tertiaryPrinter.printerName,
                useSumatraPDF: printStore.tertiaryUseSumatraPDF ?? false,
                purpose: printStore.tertiaryPrinter.purpose || undefined,
                settings: {
                  orientation: printStore.tertiaryPrinter.settings.orientation || 'landscape',
                  pageSize: {
                    height: printStore.tertiaryPrinter.settings.pageSize?.height || 105000,
                    width: printStore.tertiaryPrinter.settings.pageSize?.width || 156100
                  },
                  margins: printStore.tertiaryPrinter.settings.margins || undefined,
                  dpi: printStore.tertiaryPrinter.settings.dpi || undefined,
                  scaleFactor: printStore.tertiaryPrinter.settings.scaleFactor || undefined,
                  printBackground:
                    printStore.tertiaryPrinter.settings.printBackground || undefined,
                  printResolutionScale:
                    printStore.tertiaryPrinter.settings.printResolutionScale || undefined
                },
                frameIds: printStore.tertiaryPrinter.frameIds || []
              }
              : undefined
          },
          session: {
            hasActiveSession: !!sessionStore.id,
            mode: sessionStore.mode || null
          },
          // Include PIN and strength if available
          ...(password && password.length === 6
            ? {
              pin: password,
              pinStrength: checkPinStrength(password)
            }
            : {}),
          // Include GPS coordinates if available (server will use IP fallback if not provided)
          ...(latitude !== undefined && longitude !== undefined
            ? { latitude, longitude }
            : {})
        }

        const response = await fetch(`${process.env.VITE_API_URL}/api/app-settings-telemetry`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            telemetry,
            projectId: projectId || sessionProjectId || null
          })
        })

        if (!response.ok) {
          const errorText = await response.text()
          console.warn('[SETTINGS] Telemetry failed:', response.status, errorText)
        } else {
          // Extract telemetry ID from response and store in session store
          try {
            const responseData = await response.json()
            if (responseData.success && responseData.telemetryId) {
              const { useSessionStateStore } = await import('@renderer/stores/sessionState')
              useSessionStateStore.getState().setSessionState({
                appSettingsTelemetryId: responseData.telemetryId
              })
              console.log('[SETTINGS] Telemetry ID stored in session:', responseData.telemetryId)
            }
          } catch (parseError) {
            console.warn('[SETTINGS] Failed to parse telemetry response:', parseError)
          }
        }
      } catch (error) {
        // Silently fail - telemetry should not block user flow
        console.warn('[SETTINGS] Failed to send telemetry:', error)
      }
    }

    // Fire and forget
    sendTelemetry().catch(() => {
      // Already handled in sendTelemetry
    })

    setShowSettings(false)
  }

  // Sync auto-launch state on mount - sync store preference to system
  useEffect(() => {
    const syncAutoLaunch = async (): Promise<void> => {
      // Only sync if API is available (backward compatibility)
      const apiAl = getPreloadApi()
      if (
        !hasElectronPreload() ||
        !apiAl ||
        !('setAutoLaunchEnabled' in apiAl) ||
        typeof apiAl.setAutoLaunchEnabled !== 'function'
      ) {
        return
      }

      try {
        // Sync store preference to system (store is source of truth)
        const result = await apiAl.setAutoLaunchEnabled(autoLaunch)
        if (result.success) {
          console.log(
            `✅ [AUTO-LAUNCH] Synced store preference (${autoLaunch}) to system on app startup`
          )
        } else {
          console.warn(`⚠️ [AUTO-LAUNCH] Failed to sync store preference to system:`, result.error)
        }
      } catch (error) {
        console.error('Failed to sync auto-launch state:', error)
      }
    }

    syncAutoLaunch()
  }, []) // Only run on mount - sync store preference to system

  // Helper to check if system controls APIs are available
  const isSystemControlsAvailable = (): boolean => {
    return (
      hasElectronPreload() &&
      !!window.systemControls &&
      typeof window.systemControls.getStatus === 'function' &&
      typeof window.systemControls.apply === 'function'
    )
  }

  // Load privilege restrictions on mount
  useEffect(() => {
    const loadPrivilegeRestrictions = async (): Promise<void> => {
      const privApi = getPreloadApi()
      if (hasElectronPreload() && privApi?.getPrivilegeRestrictions) {
        try {
          const restrictions = await privApi.getPrivilegeRestrictions()
          setPrivilegeRestrictions(restrictions)
        } catch (error) {
          console.error('Failed to load privilege restrictions:', error)
        }
      }
    }

    loadPrivilegeRestrictions()
  }, [])

  // Load Windows system controls status on mount (Windows only)
  useEffect(() => {
    const loadSystemControlsStatus = async (): Promise<void> => {
      // Check if APIs are available before attempting to load status
      if (
        !hasElectronPreload() ||
        !window.systemControls ||
        typeof window.systemControls.getStatus !== 'function' ||
        typeof window.systemControls.apply !== 'function'
      ) {
        return
      }

      try {
        const status = await window.systemControls.getStatus()
        setSysControlsStatus(status)
      } catch (error) {
        console.error('Failed to load system controls status:', error)
      }
    }

    loadSystemControlsStatus()
  }, [])

  // Handle auto-launch toggle
  const handleAutoLaunchChange = async (enabled: boolean): Promise<void> => {
    // Check for API availability (backward compatibility)
    const apiLaunch = getPreloadApi()
    if (
      !hasElectronPreload() ||
      !apiLaunch ||
      !('setAutoLaunchEnabled' in apiLaunch) ||
      typeof apiLaunch.setAutoLaunchEnabled !== 'function'
    ) {
      cogoToast.error('Auto-launch is not available in this environment')
      return
    }

    try {
      const result = await apiLaunch.setAutoLaunchEnabled(enabled)
      if (result.success) {
        setAutoLaunch(enabled)
        cogoToast.success(
          enabled
            ? t('settings.autoLaunch.enabled', 'App will start at system login')
            : t('settings.autoLaunch.disabled', 'App will not start at system login')
        )
      } else {
        // Handle specific error messages
        const errorMessage = result.error || t('settings.autoLaunch.error', 'Failed to update auto-launch setting')
        if (errorMessage.includes('development mode') || errorMessage.includes('Not available in development')) {
          cogoToast.info(
            'Auto-launch is only available in production builds. This feature will work when you install the app.'
          )
        } else {
          cogoToast.error(errorMessage)
        }
        // Don't update the UI state if the operation failed
      }
    } catch (error) {
      console.error('Failed to set auto-launch:', error)
      cogoToast.error(t('settings.autoLaunch.error', 'Failed to update auto-launch setting'))
    }
  }

  // Handle Windows system controls toggles
  const handleEdgeSwipeToggle = async (enabled: boolean): Promise<void> => {
    if (!isSystemControlsAvailable()) {
      cogoToast.error('System controls not available')
      return
    }

    setSysControlsLoading((prev) => ({ ...prev, edgeSwipe: true }))
    try {
      // TypeScript: we already checked availability above
      const result = await window.systemControls!.apply({ kind: 'edge-swipe', enable: enabled })
      if (result.success) {
        // Check if script was downloaded (AppX fallback)
        if (result.filePath) {
          cogoToast.success(t('settings.systemControls.scriptDownloaded'))
        } else {
          // Setting applied directly (NSIS)
          // Reload status (only if getStatus is available)
          if (window.systemControls && typeof window.systemControls.getStatus === 'function') {
            const status = await window.systemControls.getStatus()
            setSysControlsStatus(status)
          }
          cogoToast.success(
            enabled
              ? t('settings.systemControls.edgeSwipe.explanation')
              : t('settings.systemControls.edgeSwipesEnabled')
          )
        }
      } else {
        cogoToast.error(result.error || 'Failed to update edge swipe setting')
      }
    } catch (error) {
      console.error('Error toggling edge swipe:', error)
      cogoToast.error('Failed to update edge swipe setting')
    } finally {
      setSysControlsLoading((prev) => ({ ...prev, edgeSwipe: false }))
    }
  }

  const handleLockOnWakeDisable = async (): Promise<void> => {
    if (!isSystemControlsAvailable()) {
      cogoToast.error('System controls not available')
      return
    }

    setSysControlsLoading((prev) => ({ ...prev, lockOnWakeDisable: true }))
    try {
      // TypeScript: we already checked availability above
      const result = await window.systemControls!.apply({ kind: 'lock-on-wake', enable: true })
      if (result.success) {
        cogoToast.success(t('settings.systemControls.lockOnWake.explanation'))
      } else {
        cogoToast.error(result.error || 'Failed to disable lock screen on wake')
      }
    } catch (error) {
      console.error('Error disabling lock on wake:', error)
      cogoToast.error('Failed to disable lock screen on wake')
    } finally {
      setSysControlsLoading((prev) => ({ ...prev, lockOnWakeDisable: false }))
    }
  }

  const handleLockOnWakeEnable = async (): Promise<void> => {
    if (!isSystemControlsAvailable()) {
      cogoToast.error('System controls not available')
      return
    }

    setSysControlsLoading((prev) => ({ ...prev, lockOnWakeEnable: true }))
    try {
      // TypeScript: we already checked availability above
      const result = await window.systemControls!.apply({ kind: 'lock-on-wake', enable: false })
      if (result.success) {
        // Check if script was downloaded (AppX fallback)
        if (result.filePath) {
          cogoToast.success(t('settings.systemControls.scriptDownloaded'))
        } else {
          // Setting applied directly (NSIS)
          cogoToast.success(t('settings.systemControls.lockOnWakeEnabled'))
        }
      } else {
        cogoToast.error(result.error || 'Failed to enable lock screen on wake')
      }
    } catch (error) {
      console.error('Error enabling lock on wake:', error)
      cogoToast.error('Failed to enable lock screen on wake')
    } finally {
      setSysControlsLoading((prev) => ({ ...prev, lockOnWakeEnable: false }))
    }
  }

  const handleTimeoutsEnable = async (): Promise<void> => {
    if (!isSystemControlsAvailable()) {
      cogoToast.error('System controls not available')
      return
    }

    setSysControlsLoading((prev) => ({ ...prev, timeouts: true }))
    try {
      // TypeScript: we already checked availability above
      const result = await window.systemControls!.apply({ kind: 'timeouts' })
      if (result.success) {
        // Check if script was downloaded (AppX fallback)
        if (result.filePath) {
          cogoToast.success(t('settings.systemControls.scriptDownloaded'))
        } else {
          // Setting applied directly (NSIS)
          cogoToast.success(t('settings.systemControls.timeouts.explanation'))
        }
      } else {
        cogoToast.error(result.error || 'Failed to configure power timeouts')
      }
    } catch (error) {
      console.error('Error applying timeouts:', error)
      cogoToast.error('Failed to configure power timeouts')
    } finally {
      setSysControlsLoading((prev) => ({ ...prev, timeouts: false }))
    }
  }

  // Compute toggle state from status (only for edge swipe)
  const isEdgeSwipeEnabled = sysControlsStatus?.edgeSwipe === '0' // Policy applied = disabled

  const getSelectedCameraLabel = (): string => {
    if (localTempCamera === 'detect') return t('settings.camera.detect')
    if (!localTempCamera) return t('settings.camera.selectCamera')

    const webcam = devices?.cameras.webcams.find((c) => c.deviceId === localTempCamera)
    if (webcam?.label) return webcam.label

    const usbCamera = devices?.cameras.usbCameras.find((c) => `${c.productId}` === localTempCamera)
    if (usbCamera?.brand) return usbCamera.brand

    return t('settings.camera.selectCamera')
  }

  const handleEjectCamera = async (): Promise<void> => {
    try {
      const loadingToast = cogoToast.loading(t('settings.camera.ejecting'))
      await unifiedCameraUtils.disconnect()

      // Hide the loading toast
      if (loadingToast?.hide) {
        loadingToast.hide()
      }

      setIsPreviewAvailable(false)
      setLocalTempCamera('')
      setSelectedCamera(undefined)
    } catch (err) {
      console.error('Error ejecting camera:', err)
      cogoToast.error(t('settings.camera.ejectFailed'))
    }
  }

  // Compact: centered dialog (not bottom sheet) so landscape phones stay centered; z above ControlButtons (z-[100]).
  const modalBackdropClass = useCompactModal
    ? settingsPortraitCompact
      ? 'fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-4 sm:p-5'
      : 'fixed inset-0 z-[200] flex items-center justify-center bg-black/60 p-3 sm:p-4'
    : 'fixed inset-0 z-[200] flex items-center justify-center bg-black bg-opacity-50'

  const compactCardShell =
    'relative mx-auto flex w-full max-w-lg min-h-0 max-h-[min(92dvh,calc(100dvh-1rem))] flex-col overflow-hidden rounded-2xl bg-white shadow-2xl'

  /** Same horizontal rhythm as header/footer on mobile portrait */
  const compactModalScrollClass = settingsPortraitCompact
    ? 'min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-4 py-3'
    : 'min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain px-3 py-3 sm:space-y-4 sm:px-4'

  const deviceSetupScrollClass = useCompactModal ? compactModalScrollClass : 'max-h-[80vh] space-y-4 overflow-y-auto px-4 pb-24'

  const compactHeaderPad = settingsPortraitCompact ? 'px-4 pb-3 pt-3' : useCompactModal ? 'px-3 pb-3 pt-3' : 'px-4 pb-6 pt-8'

  const footerOuterClass = useCompactModal
    ? settingsPortraitCompact
      ? 'flex shrink-0 flex-col gap-2 border-t border-gray-200 bg-white px-4 py-3'
      : 'flex shrink-0 flex-row flex-wrap items-center gap-2 border-t border-gray-200 bg-white px-3 py-3'
    : 'sticky bottom-0 z-10 flex gap-2 border-t border-gray-200 bg-white px-4 py-4'

  const footerPrimaryBtnClass = useCompactModal
    ? settingsPortraitCompact
      ? '!w-full justify-center !px-3 !py-2 !text-xs sm:!text-sm'
      : '!w-full justify-center !px-3 !py-2.5 !text-sm sm:!text-base'
    : '!px-6 !py-2 !text-base'

  const footerSecondaryBtnClass = useCompactModal
    ? settingsPortraitCompact
      ? '!w-full justify-center !px-2 !py-2 !text-[11px] sm:!text-xs'
      : 'flex-1 min-w-0 justify-center !px-2 !py-2 !text-xs sm:!text-sm'
    : '!px-6 !py-2 !text-base'

  const footerPrimaryBtnLandscapeClass = useCompactModal && !settingsPortraitCompact
    ? 'flex-1 min-w-0 justify-center !px-2 !py-2.5 !text-sm sm:!text-base'
    : ''

  const settingsFormScrollClass = useCompactModal ? compactModalScrollClass : 'flex-grow space-y-4 overflow-y-auto px-4 py-4'

  const settingsTitleClass = settingsPortraitCompact
    ? 'text-base font-bold'
    : useCompactModal
      ? 'text-lg font-bold sm:text-xl'
      : 'text-2xl font-bold'

  const settingsProjectLineClass = settingsPortraitCompact
    ? 'mb-1.5 mt-0.5 text-xs text-gray-600'
    : useCompactModal
      ? 'mb-2 mt-0.5 text-sm text-gray-600'
      : 'mb-4 mt-1 text-gray-600'

  const deviceBlockLabelClass = settingsPortraitCompact
    ? 'mb-2 block text-xs font-medium text-gray-700'
    : useCompactModal
      ? 'mb-2 block text-sm font-medium text-gray-700'
      : 'mb-2 block text-lg font-medium text-gray-700'

  const formLabelClass = settingsPortraitCompact
    ? 'mb-2 block text-xs font-medium text-gray-700'
    : 'mb-2 block text-sm font-medium text-gray-700'

  const cameraSelectTriggerClass =
    'flex w-full min-w-0 cursor-auto items-center justify-between gap-2 border border-gray-300 rounded-md bg-white p-2 text-left focus:border-blue-500 focus:ring-2 focus:ring-blue-500'

  const cameraSelectValueClass = settingsPortraitCompact
    ? 'min-w-0 flex-1 truncate text-sm'
    : useCompactModal
      ? 'min-w-0 flex-1 truncate text-base'
      : 'min-w-0 flex-1 truncate text-lg'

  const dropdownItemClass = settingsPortraitCompact
    ? 'cursor-pointer truncate p-2 text-xs hover:bg-blue-500 hover:text-white'
    : useCompactModal
      ? 'cursor-pointer truncate p-2 text-sm hover:bg-blue-500 hover:text-white'
      : 'cursor-pointer p-2 text-lg hover:bg-blue-500 hover:text-white'

  const dropdownItemMutedClass = settingsPortraitCompact
    ? 'cursor-pointer truncate p-2 text-xs text-gray-500 hover:bg-blue-500 hover:text-white'
    : useCompactModal
      ? 'cursor-pointer truncate p-2 text-sm text-gray-500 hover:bg-blue-500 hover:text-white'
      : 'cursor-pointer p-2 text-lg text-gray-500 hover:bg-blue-500 hover:text-white'

  const settingsInsetCardClass = settingsPortraitCompact
    ? 'flex items-center justify-between rounded-md border border-gray-200 p-2.5'
    : 'flex items-center justify-between rounded-md border border-gray-200 p-3'

  const printerToggleRowClass = `mb-1 ${settingsInsetCardClass}`

  const settingsHintClass = settingsPortraitCompact ? 'mt-1 text-xs text-gray-600' : 'mt-1 text-sm text-gray-600'

  const settingsMutedLineClass = settingsPortraitCompact ? 'mt-1 text-xs text-gray-500' : 'mt-1 text-sm text-gray-500'
  const printerSelectClass = `w-full min-w-0 truncate rounded-md border border-gray-300 p-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`
  const printerSelectValue =
    selectedPrinterTarget === 'remote' && selectedRemotePrinterMachineId
      ? `remote:${selectedRemotePrinterMachineId}`
      : localTempPrinter
        ? `local:${localTempPrinter}`
        : 'default'

  const creditsChipClass = useCompactModal
    ? settingsPortraitCompact
      ? 'flex items-center gap-1 rounded-lg bg-gray-50 px-2 py-1'
      : 'flex items-center gap-1.5 rounded-lg bg-gray-50 px-2 py-1.5'
    : 'flex items-center gap-3 rounded-lg bg-gray-50 px-4 py-2'

  const preloadForAutoLaunch = getPreloadApi()
  const autoLaunchApiAvailable =
    hasElectronPreload() &&
    !!preloadForAutoLaunch &&
    'setAutoLaunchEnabled' in preloadForAutoLaunch &&
    typeof preloadForAutoLaunch.setAutoLaunchEnabled === 'function'

  return (
    <div className={modalBackdropClass}>
      {showCameraPrompt ? (
        <CameraPrompt onStart={handleCameraPromptStart} />
      ) : isFullscreen ? (
        <div
          className={
            useCompactModal
              ? compactCardShell
              : 'max-h-[90%] w-full max-w-xl rounded-lg bg-white px-4 py-8'
          }
          style={
            useCompactModal
              ? {
                paddingBottom: 'max(env(safe-area-inset-bottom), 0.25rem)'
              }
              : undefined
          }
          data-operator-area
        >
          <div
            className={`sticky top-0 z-10 shrink-0 border-b border-gray-200 bg-white ${compactHeaderPad}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h2 className={settingsTitleClass}>
                  {t('settings.title')}
                </h2>
                {projectTitle && (
                  <p className={settingsProjectLineClass}>
                    {t('settings.project')}: {projectTitle}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <LanguageSelector />
                  <button
                    onClick={async () => {
                      await handleEjectCamera()
                      await fetchDevices()
                      window.location.reload()
                    }}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title={t('common.refresh')}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="shrink-0 text-left">
                <div className={creditsChipClass}>
                  {!isKlakKlikKluk && (
                    <div>
                      <p className={`text-gray-500 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>{t('settings.credits.title')}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col">
                          <span
                            className={`font-bold text-blue-600 ${settingsPortraitCompact ? 'text-sm' : useCompactModal ? 'text-base sm:text-lg' : 'text-xl'
                              }`}
                          >
                            {credits}
                          </span>
                        </div>
                        <button
                          onClick={fetchSubscriptionAndCredits}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title={t('settings.credits.refresh')}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col text-center">
                    {displayType !== 'free' && (
                      <div
                        className={`${settingsPortraitCompact
                          ? 'rounded px-1.5 py-0.5 text-[10px] font-medium'
                          : 'rounded px-2 py-1 text-sm font-medium'
                          } ${displayType.toLowerCase() === 'enterprise'
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
                            : displayType.toLowerCase() === 'professional'
                              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                              : displayType.toLowerCase() === 'protrial'
                                ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white'
                                : displayType.toLowerCase() === 'starter'
                                  ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white'
                                  : 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
                          }`}
                      >
                        {displayType.charAt(0).toUpperCase() + displayType.slice(1)}
                      </div>
                    )}
                    {displayType?.toLowerCase() === 'protrial' && remainingTime && (
                      <span className="text-xs text-gray-500 mt-1">({remainingTime})</span>
                    )}
                  </div>
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className={`flex items-center rounded-lg text-white transition-colors ${settingsPortraitCompact ? 'gap-1 px-2 py-1' : 'gap-2 px-3 py-1.5'
                      }
                      ${displayType.toLowerCase() === 'enterprise'
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-indigo-600 hover:to-purple-700'
                        : displayType.toLowerCase() === 'professional'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600'
                          : displayType.toLowerCase() === 'protrial'
                            ? 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600'
                            : displayType.toLowerCase() === 'starter'
                              ? 'bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600'
                              : 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700'
                      }`}
                    title={`${displayType && displayType.toLowerCase() !== 'free' ? displayType : t('common.upgrade', 'Upgrade')} ${t('projectNav.subscription', 'subscription')}`}
                  >
                    <FaCrown className={settingsPortraitCompact ? 'h-4 w-4 text-xs' : 'h-5 w-5 text-sm'} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className={deviceSetupScrollClass}>
            <div>
              <label className={deviceBlockLabelClass}>
                {t('settings.camera.title')}
                {isEditCustomPath && (
                  <span className={`ml-2 font-normal text-gray-500 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>(Optional)</span>
                )}
              </label>
              <div className="w-full aspect-video bg-black mb-2 flex items-center justify-center">
                {isPreviewAvailable ? (
                  <Preview
                    key={`preview-${cameraSettings.captureRatio}-${cameraSettings.mirror}-${cameraSettings.previewRotation}-${cameraSettings.previewQuality?.maxWidth}-${cameraSettings.previewQuality?.maxHeight}-${cameraSettings.previewQuality?.quality}-${cameraSettings.previewQuality?.resizeQuality}`}
                  />
                ) : (
                  <div className="text-white flex flex-col items-center">
                    <FaCamera className="mb-2" />
                    <span>{t('settings.camera.noPreview')}</span>
                  </div>
                )}
              </div>
              <div className="flex min-w-0 gap-2">
                <div className="relative min-w-0 flex-1" ref={dropdownRef}>
                  <button
                    type="button"
                    title={getSelectedCameraLabel()}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={cameraSelectTriggerClass}
                  >
                    <span className={cameraSelectValueClass}>{getSelectedCameraLabel()}</span>
                    <svg className="h-4 w-4 shrink-0 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute z-10 mt-1 max-h-[min(50vh,16rem)] w-full overflow-y-auto overflow-x-hidden rounded-md border border-gray-300 bg-white shadow-lg">
                      {!devices?.cameras.webcams?.length && !devices?.cameras.usbCameras?.length ? (
                        <div
                          className={dropdownItemClass}
                          onClick={(e) => {
                            handleCameraChange('detect', e.nativeEvent)
                            setIsDropdownOpen(false)
                          }}
                        >
                          {t('settings.camera.detect')}
                        </div>
                      ) : (
                        <>
                          {devices?.cameras.webcams?.length > 0 && devices.cameras.webcams[0] && (
                            <div
                              className={dropdownItemMutedClass}
                              onClick={(e) => {
                                handleCameraChange(
                                  devices.cameras.webcams[0].deviceId,
                                  e.nativeEvent
                                )
                                setIsDropdownOpen(false)
                              }}
                            >
                              {t('settings.camera.defaultCamera')}{' '}
                              {devices.cameras.webcams[0].label &&
                                `(${devices.cameras.webcams[0].label})`}
                            </div>
                          )}
                          {devices?.cameras.webcams?.map((webcam) => (
                            <div
                              key={webcam.deviceId}
                              className={dropdownItemClass}
                              onClick={(e) => {
                                handleCameraChange(webcam.deviceId, e.nativeEvent)
                                setIsDropdownOpen(false)
                              }}
                            >
                              {webcam.label || t('settings.camera.unknownWebcam')}
                            </div>
                          ))}
                          {capabilities.usbCamera && devices?.cameras.usbCameras?.map((camera) => (
                            <div
                              key={`${camera.vendorId}-${camera.productId}`}
                              className={dropdownItemClass}
                              onClick={(e) => {
                                handleCameraChange(`${camera.productId}`, e.nativeEvent)
                                setIsDropdownOpen(false)
                              }}
                            >
                              {camera.brand} (
                              {camera.hasDriver
                                ? t('settings.camera.driverStatus.installed')
                                : t('settings.camera.driverStatus.uninstalled')})
                            </div>
                          ))}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {isEditCustomPath ? (
                <div className={settingsHintClass}>
                  {t('settings.camera.editModeOptional')}
                </div>
              ) : hasElectronPreload() ? (
                <div className={settingsHintClass}>
                  {hasConnectionError ? (
                    <>
                      {t('settings.camera.troubleshoot')}{' '}
                      <a
                        onClick={async () => {
                          await handleEjectCamera()
                          window.location.reload()
                        }}
                        className="cursor-pointer text-blue-500"
                      >
                        {t('common.refresh')}
                      </a>
                    </>
                  ) : (
                    <>
                      {t('settings.camera.guideDescriptionPrefix')}{' '}
                      <span
                        onClick={() => {
                          setShowGuideModal(true)
                        }}
                        className="text-blue-500 cursor-pointer"
                      >
                        {t('settings.camera.guideLink')}
                      </span>{' '}
                      {t('settings.camera.guideDescriptionSuffix')}
                    </>
                  )}
                </div>
              ) : (
                <div className={settingsHintClass}>
                  {t('settings.camera.dslrInstruction')}
                  {'. '}
                  <span
                    onClick={() => {
                      setShowGuideModal(true)
                    }}
                    className="text-blue-500 cursor-pointer"
                  >
                    {t('settings.camera.instructionLink')}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className={footerOuterClass}>
            {useCompactModal ? (
              settingsPortraitCompact ? (
                <>
                  <div className="grid w-full grid-cols-2 gap-2">
                    <Button
                      text={
                        <div className="flex items-center justify-center gap-1">
                          <FaChevronLeft size={10} className="shrink-0" />
                          <span className="truncate">{t('settings.buttons.seeAllProjects')}</span>
                        </div>
                      }
                      variant="secondary"
                      onClick={async () => {
                        await unifiedCameraUtils.disconnect()
                        window.location.href = '/'
                        setDefaultProjectId(undefined)
                      }}
                      className={footerSecondaryBtnClass}
                    />
                    <Button
                      text={
                        <div className="flex items-center justify-center gap-1">
                          <span className="truncate">{t('settings.buttons.editProject')}</span>
                          <FaExternalLinkAlt size={10} className="shrink-0" />
                        </div>
                      }
                      variant="secondary"
                      onClick={() =>
                        void openExternalUrl(
                          `${isKlakKlikKluk ? 'https://admin.klakklikkluk.com' : process.env.VITE_API_URL
                          }/dashboard/projects/${projectId}`
                        )
                      }
                      className={footerSecondaryBtnClass}
                    />
                  </div>
                  <Button
                    text={t('settings.buttons.continue')}
                    onClick={async (e) => {
                      if (!isEditCustomPath && localTempCamera) {
                        await handleCameraChange(localTempCamera, e.nativeEvent)
                      }
                      handleContinue()
                    }}
                    className={`${footerPrimaryBtnClass} ${!isEditCustomPath && !isPreviewAvailable
                      ? '!bg-gray-300 !text-gray-500 opacity-40 cursor-not-allowed'
                      : ''
                      }`}
                  />
                </>
              ) : (
                <>
                  <Button
                    text={
                      <div className="flex items-center justify-center gap-1">
                        <FaChevronLeft size={12} className="shrink-0" />
                        <span className="truncate">{t('settings.buttons.seeAllProjects')}</span>
                      </div>
                    }
                    variant="secondary"
                    onClick={async () => {
                      await unifiedCameraUtils.disconnect()
                      window.location.href = '/'
                      setDefaultProjectId(undefined)
                    }}
                    className={footerSecondaryBtnClass}
                  />
                  <Button
                    text={
                      <div className="flex items-center justify-center gap-1">
                        <span className="truncate">{t('settings.buttons.editProject')}</span>
                        <FaExternalLinkAlt size={12} className="shrink-0" />
                      </div>
                    }
                    variant="secondary"
                    onClick={() =>
                      void openExternalUrl(
                        `${isKlakKlikKluk ? 'https://admin.klakklikkluk.com' : process.env.VITE_API_URL
                        }/dashboard/projects/${projectId}`
                      )
                    }
                    className={footerSecondaryBtnClass}
                  />
                  <Button
                    text={t('settings.buttons.continue')}
                    onClick={async (e) => {
                      if (!isEditCustomPath && localTempCamera) {
                        await handleCameraChange(localTempCamera, e.nativeEvent)
                      }
                      handleContinue()
                    }}
                    className={`${footerPrimaryBtnLandscapeClass || footerPrimaryBtnClass} ${!isEditCustomPath && !isPreviewAvailable
                      ? '!bg-gray-300 !text-gray-500 opacity-40 cursor-not-allowed'
                      : ''
                      }`}
                  />
                </>
              )
            ) : (
              <>
                <Button
                  text={
                    <div className="flex items-center gap-2">
                      <FaChevronLeft size={14} />
                      {t('settings.buttons.seeAllProjects')}
                    </div>
                  }
                  variant="secondary"
                  onClick={async () => {
                    await unifiedCameraUtils.disconnect()
                    window.location.href = '/'
                    setDefaultProjectId(undefined)
                  }}
                  className="!px-6 !py-2 !text-base"
                />
                <Button
                  text={
                    <div className="flex items-center gap-2">
                      {t('settings.buttons.editProject')}
                      <FaExternalLinkAlt size={14} />
                    </div>
                  }
                  variant="secondary"
                  onClick={() =>
                    void openExternalUrl(
                      `${isKlakKlikKluk ? 'https://admin.klakklikkluk.com' : process.env.VITE_API_URL
                      }/dashboard/projects/${projectId}`
                    )
                  }
                  className="!px-6 !py-2 !text-base"
                />
                <Button
                  text={t('settings.buttons.continue')}
                  onClick={async (e) => {
                    if (!isEditCustomPath && localTempCamera) {
                      await handleCameraChange(localTempCamera, e.nativeEvent)
                    }
                    handleContinue()
                  }}
                  className={`!px-6 !py-2 !text-base ${!isEditCustomPath && !isPreviewAvailable
                    ? '!bg-gray-300 !text-gray-500 opacity-40 cursor-not-allowed'
                    : ''
                    }`}
                />
              </>
            )}
          </div>
        </div>
      ) : (
        <div
          className={
            useCompactModal
              ? compactCardShell
              : 'flex max-h-[90%] w-full max-w-xl flex-col rounded-lg bg-white px-4'
          }
          style={
            useCompactModal
              ? {
                paddingBottom: 'max(env(safe-area-inset-bottom), 0.25rem)'
              }
              : { maxHeight: '90%' }
          }
          data-operator-area
        >
          <div
            className={`sticky top-0 z-10 shrink-0 border-b border-gray-200 bg-white ${compactHeaderPad}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <h2 className={settingsTitleClass}>
                  {t('settings.title')}
                </h2>
                {projectTitle && (
                  <p className={settingsProjectLineClass}>
                    {t('settings.project')}: {projectTitle}
                  </p>
                )}
                <div className="flex flex-wrap items-center gap-2">
                  <LanguageSelector />
                  <button
                    onClick={async () => {
                      await handleEjectCamera()
                      await fetchDevices()
                      window.location.reload()
                    }}
                    className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                    title={t('common.refresh')}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-4 w-4"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="shrink-0 text-left">
                <div className={creditsChipClass}>
                  {!isKlakKlikKluk && (
                    <div>
                      <p className={`text-gray-500 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>{t('settings.credits.title')}</p>
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col">
                          <span
                            className={`font-bold text-blue-600 ${settingsPortraitCompact ? 'text-sm' : useCompactModal ? 'text-base sm:text-lg' : 'text-xl'
                              }`}
                          >
                            {credits}
                          </span>
                        </div>
                        <button
                          onClick={fetchSubscriptionAndCredits}
                          className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                          title={t('settings.credits.refresh')}
                        >
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-4 w-4"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </div>
                    </div>
                  )}
                  <div className="flex flex-col text-center">
                    {displayType !== 'free' && (
                      <div
                        className={`${settingsPortraitCompact
                          ? 'rounded px-1.5 py-0.5 text-[10px] font-medium'
                          : 'rounded px-2 py-1 text-sm font-medium'
                          } ${displayType.toLowerCase() === 'enterprise'
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
                            : displayType.toLowerCase() === 'professional'
                              ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white'
                              : displayType.toLowerCase() === 'protrial'
                                ? 'bg-gradient-to-r from-yellow-500 to-amber-500 text-white'
                                : displayType.toLowerCase() === 'starter'
                                  ? 'bg-gradient-to-r from-green-500 to-teal-500 text-white'
                                  : 'bg-gradient-to-r from-gray-500 to-gray-600 text-white'
                          }`}
                      >
                        {displayType.charAt(0).toUpperCase() + displayType.slice(1)}
                      </div>
                    )}
                    {displayType?.toLowerCase() === 'protrial' && remainingTime && (
                      <span className="text-xs text-gray-500 mt-1">({remainingTime})</span>
                    )}
                  </div>
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className={`flex items-center rounded-lg text-white transition-colors ${settingsPortraitCompact ? 'gap-1 px-2 py-1' : 'gap-2 px-3 py-1.5'
                      }
                      ${displayType.toLowerCase() === 'enterprise'
                        ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-indigo-600 hover:to-purple-700'
                        : displayType.toLowerCase() === 'professional'
                          ? 'bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600'
                          : displayType.toLowerCase() === 'protrial'
                            ? 'bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600'
                            : displayType.toLowerCase() === 'starter'
                              ? 'bg-gradient-to-r from-green-500 to-teal-500 hover:from-green-600 hover:to-teal-600'
                              : 'bg-gradient-to-r from-gray-500 to-gray-600 hover:from-gray-600 hover:to-gray-700'
                      }`}
                    title={`${displayType && displayType.toLowerCase() !== 'free' ? displayType : t('common.upgrade', 'Upgrade')} ${t('projectNav.subscription', 'subscription')}`}
                  >
                    <FaCrown className={settingsPortraitCompact ? 'h-4 w-4 text-xs' : 'h-5 w-5 text-sm'} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className={settingsFormScrollClass}>
            <div>
              <label className={formLabelClass}>
                {t('settings.camera.title')}
                {isEditCustomPath && (
                  <span className={`ml-2 font-normal text-gray-500 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>(Optional)</span>
                )}
              </label>
              <div className="w-full aspect-video bg-black mb-2 flex items-center justify-center">
                {isPreviewAvailable ? (
                  <div className="relative w-full h-full">
                    <Preview
                      key={`preview-${cameraSettings.captureRatio}-${cameraSettings.mirror}-${cameraSettings.previewRotation}`}
                    />
                    <div className="absolute top-2 right-2 z-50 flex items-center gap-2 bg-black bg-opacity-50 px-2 py-1 rounded">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={debugFps}
                          onChange={() => setDebugFps(!debugFps)}
                          aria-label="Toggle debug FPS"
                        />
                        <div
                          className={toggleSwitchClasses}
                          style={{ backgroundColor: debugFps ? primaryColor : undefined }}
                        />
                      </label>
                      <span className="text-xs text-white">FPS</span>
                    </div>
                  </div>
                ) : (
                  <div className="text-white flex flex-col items-center">
                    <FaCamera className="mb-2" />
                    <span>{t('settings.camera.noPreview')}</span>
                  </div>
                )}
              </div>
              <div className="flex min-w-0 gap-2">
                <div className="relative min-w-0 flex-1" ref={dropdownRef}>
                  <button
                    type="button"
                    title={getSelectedCameraLabel()}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={cameraSelectTriggerClass}
                  >
                    <span className={cameraSelectValueClass}>{getSelectedCameraLabel()}</span>
                    <svg className="h-4 w-4 shrink-0 text-gray-500" viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </button>
                  {isDropdownOpen && (
                    <div className="absolute z-10 mt-1 max-h-[min(50vh,16rem)] w-full overflow-y-auto overflow-x-hidden rounded-md border border-gray-300 bg-white shadow-lg">
                      {!devices?.cameras.webcams?.length && !devices?.cameras.usbCameras?.length ? (
                        <div
                          className={dropdownItemClass}
                          onClick={(e) => {
                            handleCameraChange('detect', e.nativeEvent)
                            setIsDropdownOpen(false)
                          }}
                        >
                          {t('settings.camera.detect')}
                        </div>
                      ) : (
                        <>
                          {devices?.cameras.webcams?.length > 0 && devices.cameras.webcams[0] && (
                            <div
                              className={dropdownItemMutedClass}
                              onClick={(e) => {
                                handleCameraChange(
                                  devices.cameras.webcams[0].deviceId,
                                  e.nativeEvent
                                )
                                setIsDropdownOpen(false)
                              }}
                            >
                              {t('settings.camera.defaultCamera')}{' '}
                              {devices.cameras.webcams[0].label &&
                                `(${devices.cameras.webcams[0].label})`}
                            </div>
                          )}
                          {devices?.cameras.webcams?.map((webcam) => (
                            <div
                              key={webcam.deviceId}
                              className={dropdownItemClass}
                              onClick={(e) => {
                                handleCameraChange(webcam.deviceId, e.nativeEvent)
                                setIsDropdownOpen(false)
                              }}
                            >
                              {webcam.label || t('settings.camera.unknownWebcam')}
                            </div>
                          ))}
                          {capabilities.usbCamera && devices?.cameras.usbCameras?.map((camera) => (
                            <div
                              key={`${camera.vendorId}-${camera.productId}`}
                              className={dropdownItemClass}
                              onClick={(e) => {
                                handleCameraChange(`${camera.productId}`, e.nativeEvent)
                                setIsDropdownOpen(false)
                              }}
                            >
                              {camera.brand} (
                              {camera.hasDriver
                                ? t('settings.camera.driverStatus.installed')
                                : t('settings.camera.driverStatus.uninstalled')})
                            </div>
                          ))}
                          <div
                            className={dropdownItemClass}
                            onClick={(e) => {
                              handleCameraChange('detect', e.nativeEvent)
                              setIsDropdownOpen(false)
                            }}
                          >
                            {t('settings.camera.detect')}
                          </div>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <button onClick={fetchDevices} title={t('common.refresh')}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                {localTempCamera &&
                  devices?.cameras.usbCameras.find((c) => `${c.productId}` === localTempCamera) && (
                    <button
                      onClick={async () => {
                        const camera = devices.cameras.usbCameras.find(
                          (c) => `${c.productId}` === localTempCamera
                        )
                        if (camera) {
                          await handleEjectCamera()
                        }
                      }}
                      title={t('settings.camera.eject')}
                      className="text-red-500 hover:text-red-700"
                    >
                      <FaEject className="h-4 w-4" />
                    </button>
                  )}
                <Button
                  text="..."
                  onClick={() => {
                    setShowCameraSettings(true)
                    setIsPreviewAvailable(false)
                  }}
                  className={settingsPortraitCompact ? '!px-2 !py-1.5 !text-xs' : '!px-3 !py-2 !text-base'}
                  title={t('settings.camera.settings')}
                />
              </div>
              {isEditCustomPath ? (
                <div className={settingsHintClass}>
                  {t('settings.camera.editModeOptional')}
                </div>
              ) : hasElectronPreload() ? (
                <div className={settingsHintClass}>
                  {hasConnectionError ? (
                    <>
                      {t('settings.camera.troubleshoot')}{' '}
                      <a
                        onClick={async () => {
                          await handleEjectCamera()
                          window.location.reload()
                        }}
                        className="cursor-pointer text-blue-500"
                      >
                        {t('common.refresh')}
                      </a>
                    </>
                  ) : (
                    <>
                      {t('settings.camera.guideDescriptionPrefix')}{' '}
                      <span
                        onClick={() => {
                          setShowGuideModal(true)
                        }}
                        className="text-blue-500 cursor-pointer"
                      >
                        {t('settings.camera.guideLink')}
                      </span>{' '}
                      {t('settings.camera.guideDescriptionSuffix')}
                    </>
                  )}
                </div>
              ) : (
                <div className={settingsHintClass}>
                  {t('settings.camera.dslrInstruction')}
                  {'. '}
                  <span
                    onClick={() => {
                      setShowGuideModal(true)
                    }}
                    className="text-blue-500 cursor-pointer"
                  >
                    {t('settings.camera.instructionLink')}
                  </span>
                </div>
              )}
            </div>

            {/* Printer: show enable toggle everywhere; printer list only when Electron discovers devices */}
            <div>
              <label className={formLabelClass}>
                {t('settings.printer.title')}
              </label>
              <div className={printerToggleRowClass}>
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <FaPrint className="shrink-0 text-gray-500" />
                  <span className={`truncate ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>{t('settings.printer.enablePrinting')}</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isPrintingEnabled}
                    onChange={() => togglePrinting()}
                    aria-label={t('settings.printer.enablePrinting')}
                  />
                  <div
                    className={toggleSwitchClasses}
                    style={{ backgroundColor: isPrintingEnabled ? primaryColor : undefined }}
                  />
                </label>
              </div>
              {!hasElectronPreload() && !isInTauriShell() && (
                <p className={settingsMutedLineClass}>{t('settings.printer.webPrintHint')}</p>
              )}
              <div className="flex min-w-0 gap-2">
                <div className="flex-1 min-w-0">
                  <select
                    value={printerSelectValue}
                    onChange={handleUnifiedPrinterChange}
                    title={printerSelectValue}
                    className={printerSelectClass}
                    aria-label={t('settings.printer.selectPrinter')}
                  >
                    <option value="default">
                      {t('settings.printer.defaultSystemDialog', 'Default (System Dialog)')}
                    </option>
                    {hasElectronPreload() && (devices?.printers?.length ?? 0) > 0 && (
                      <optgroup label={t('settings.printer.localPrinters', 'This computer')}>
                        {devices?.printers.map((printer) => (
                          <option key={`local:${printer}`} value={`local:${printer}`}>
                            {printer}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {remotePrinters.length > 0 && (
                      <optgroup label={t('settings.printer.remotePrinters', 'Remote printers')}>
                        {remotePrinters.map((rp) => (
                          <option
                            key={`remote:${rp.machineIdentifier}`}
                            value={`remote:${rp.machineIdentifier}`}
                          >
                            {(rp.projectName || rp.printerName) +
                              (rp.isAvailable ? '' : ' (offline)')}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                </div>
                {(hasElectronPreload() || selectedPrinterTarget === 'remote') && (
                  <Button
                    text="..."
                    onClick={() =>
                      selectedPrinterTarget === 'remote'
                        ? setShowRemotePrintSettings(true)
                        : setShowPrinterSettings(true)
                    }
                    className={settingsPortraitCompact ? '!px-2 !py-1.5 !text-xs' : '!px-3 !py-2 !text-base'}
                    title={t('settings.printer.settings')}
                  />
                )}
              </div>
              <div className={`mt-1 text-gray-600 ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>
                {t('settings.printer.guideDescriptionPrefix')}{' '}
                <span
                  onClick={() => setShowPrinterGuideModal(true)}
                  className="text-blue-500 cursor-pointer"
                >
                  {t('settings.printer.guideLink')}
                </span>{' '}
                {t('settings.printer.guideDescriptionSuffix')}
              </div>
            </div>

            <div>
              <label className={formLabelClass}>
                {t('settings.download.title')}
              </label>
              <div className={settingsInsetCardClass}>
                <div className="flex min-w-0 flex-1 items-center gap-2">
                  <FaDownload className="shrink-0 text-gray-500" />
                  <span className={`truncate ${settingsPortraitCompact ? 'text-xs' : 'text-sm'}`}>{t('settings.download.saveLocal')}</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={downloadLocal}
                    onChange={(e) => setDownloadLocal(e.target.checked)}
                    aria-label="Toggle local downloads"
                  />
                  <div
                    className={toggleSwitchClasses}
                    style={{ backgroundColor: downloadLocal ? primaryColor : undefined }}
                  />
                </label>
              </div>
              <p className={settingsMutedLineClass}>
                {t(
                  'settings.download.explanation',
                  'Your photos and videos are saved to your local storage, and can be found on your computer.'
                )}
              </p>

              {hasElectronPreload() &&
                getPreloadApi() &&
                typeof getPreloadApi()!.showOpenDirectoryDialog === 'function' && (
                  <div className={`mt-2 rounded-md border border-gray-200 ${settingsPortraitCompact ? 'p-2.5' : 'p-3'}`}>
                    <label className={formLabelClass} htmlFor="settings-download-destination-path">
                      {t('settings.download.customDestination', 'Save Destination')}
                    </label>
                    <p className="mb-2 text-xs text-gray-500">
                      {t('settings.download.destinationFormat', 'Files will be saved in: [Destination]/YYYY-MM-DD/')}
                    </p>
                    <div className="flex gap-2 items-center">
                      <input
                        id="settings-download-destination-path"
                        type="text"
                        readOnly
                        value={downloadDestination || t('settings.download.defaultPath', 'Default (Pictures/Dreambooth Gallery)')}
                        className="flex-1 p-2 border border-gray-300 rounded-md text-gray-600 bg-gray-50 text-sm"
                        title={t('settings.download.customDestination', 'Save Destination')}
                      />
                      <Button
                        text={t('common.browse', 'Browse...')}
                        onClick={async () => {
                          try {
                            const fs = getPreloadApi()
                            if (!fs?.showOpenDirectoryDialog) return
                            const newPath = await fs.showOpenDirectoryDialog()
                            if (newPath) {
                              await fs.setDownloadDestination(newPath)
                              setDownloadDestination(newPath)
                              cogoToast.success(t('settings.download.pathUpdated', 'Save destination updated'))
                            }
                          } catch (error) {
                            console.error('Failed to set download destination:', error)
                            cogoToast.error('Failed to update destination')
                          }
                        }}
                        className="!px-3 !py-2 !text-sm whitespace-nowrap"
                      />
                      {downloadDestination && (
                        <Button
                          text={t('common.reset', 'Reset')}
                          variant="secondary"
                          onClick={async () => {
                            try {
                              const fs = getPreloadApi()
                              if (fs?.setDownloadDestination) await fs.setDownloadDestination(undefined)
                              setDownloadDestination(undefined)
                              cogoToast.success(t('settings.download.pathReset', 'Save destination reset to default'))
                            } catch (error) {
                              console.error('Failed to reset download destination:', error)
                              cogoToast.error('Failed to reset destination')
                            }
                          }}
                          className="!px-3 !py-2 !text-sm !text-red-600 border border-red-200 hover:bg-red-50 !bg-transparent"
                        />
                      )}
                    </div>
                  </div>
                )}
            </div>

            <div>
              <label className={formLabelClass}>
                {t('settings.pin.title')}
              </label>
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <input
                    title={t('settings.pin.placeholder')}
                    placeholder={t('settings.pin.placeholder')}
                    type={password ? (showPassword ? 'text' : 'password') : 'text'}
                    value={password || t('settings.pin.noPin')}
                    readOnly
                    className={`w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 pr-10 ${password ? '' : 'text-gray-500'
                      }`}
                  />
                  <button
                    title="Toggle password visibility"
                    aria-label="Toggle password visibility"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? <FaEyeSlash /> : <FaEye />}
                  </button>
                </div>
                <Button
                  text={t('settings.pin.change')}
                  onClick={handleChangePinClick}
                  className={settingsPortraitCompact ? '!px-3 !py-1.5 !text-xs' : '!px-4 !py-2 !text-sm'}
                />
              </div>
            </div>

            <div>
              <label className={formLabelClass}>
                {t('settings.autoStart.title')}
              </label>
              <div className={settingsInsetCardClass}>
                <div className="flex items-center gap-2">
                  <FaPlay className="text-gray-500" />
                  <span className={settingsPortraitCompact ? 'text-xs' : 'text-sm'}>{t('settings.autoStart.withProject')}</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={autoStart}
                    onChange={() => {
                      setAutoStart(!autoStart)
                      if (!autoStart && projectId) {
                        setDefaultProjectId(projectId)
                      } else if (autoStart) {
                        setDefaultProjectId(undefined)
                      }
                    }}
                    aria-label={t('settings.autoStart.withProject')}
                  />
                  <div
                    className={toggleSwitchClasses}
                    style={{ backgroundColor: autoStart ? primaryColor : undefined }}
                  />
                </label>
              </div>
              <p className={settingsMutedLineClass}>{t('settings.autoStart.explanation')}</p>
            </div>

            <div>
              <label className={formLabelClass}>
                {t('settings.screensaver.title')}
              </label>
              <div className={settingsInsetCardClass}>
                <div className="flex items-center gap-2">
                  <FaEye className="text-gray-500" />
                  <span className={settingsPortraitCompact ? 'text-xs' : 'text-sm'}>{t('settings.screensaver.label')}</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={config?.theme?.enableScreensaver ?? enableScreensaver}
                    onChange={(e) => setSetting('enableScreensaver', e.target.checked)}
                    disabled={config?.theme?.enableScreensaver !== undefined}
                    aria-label={t('settings.screensaver.label')}
                  />
                  <div
                    className={toggleSwitchClasses}
                    style={{
                      backgroundColor: (config?.theme?.enableScreensaver ?? enableScreensaver) ? primaryColor : undefined,
                      opacity: config?.theme?.enableScreensaver !== undefined ? 0.6 : 1
                    }}
                  />
                </label>
              </div>
              {config?.theme?.enableScreensaver !== undefined && (
                <p className="mt-2 text-sm text-blue-600">
                  {t('settings.screensaver.controlledByProject')}
                </p>
              )}
              {(config?.theme?.enableScreensaver ?? enableScreensaver) && (
                <>
                  {!config?.theme?.screensaverVideoUrl && (
                    <p className="mt-2 text-sm text-amber-600">
                      {t('settings.screensaver.noVideo')}
                    </p>
                  )}
                  {config?.theme?.idleTimeout ? (
                    <p className="mt-2 text-sm text-gray-600">
                      {t('settings.screensaver.usingProjectTimeout')} <span className="font-medium">{config.theme.idleTimeout} {t('settings.screensaver.seconds')}</span> {t('settings.screensaver.configuredInDashboard')}
                    </p>
                  ) : (
                    <div className="mt-2 flex items-center gap-2">
                      <span className="text-sm text-gray-600">{t('settings.screensaver.timeout')}</span>
                      <input
                        title={"Timeout in seconds"}
                        placeholder={"Timeout in seconds"}
                        type="number"
                        min={10}
                        max={3600}
                        value={screensaverTimeout}
                        onChange={(e) => setSetting('screensaverTimeout', parseInt(e.target.value) || 60)}
                        className="w-24 p-1 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  )}
                </>
              )}
              <p className={settingsMutedLineClass}>
                {t('settings.screensaver.explanation')}
              </p>
            </div>

            {!hasElectronPreload() && !isInTauriShell() && (
              <div>
                <label className={formLabelClass}>{t('settings.browserGestures.title')}</label>
                <div className={settingsInsetCardClass}>
                  <div className="flex items-center gap-2">
                    <FaExternalLinkAlt className="text-gray-500" />
                    <span className={settingsPortraitCompact ? 'text-xs' : 'text-sm'}>
                      {t('settings.browserGestures.label')}
                    </span>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={suppressBrowserGestures}
                      onChange={(e) => setSuppressBrowserGestures(e.target.checked)}
                      aria-label={t('settings.browserGestures.label')}
                    />
                    <div
                      className={toggleSwitchClasses}
                      style={{
                        backgroundColor: suppressBrowserGestures ? primaryColor : undefined
                      }}
                    />
                  </label>
                </div>
                <p className={settingsMutedLineClass}>{t('settings.browserGestures.explanation')}</p>
              </div>
            )}

            {/* Auto-Launch - Only show if API is available (backward compatibility) */}
            {autoLaunchApiAvailable && (
              <div>
                <label className={formLabelClass}>
                  {t('settings.autoLaunch.title')}
                </label>
                <div className={settingsInsetCardClass}>
                  <div className="flex items-center gap-2">
                    <FaRocket className="text-gray-500" />
                    <span className={settingsPortraitCompact ? 'text-xs' : 'text-sm'}>{t('settings.autoLaunch.label')}</span>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={autoLaunch}
                      onChange={(e) => {
                        handleAutoLaunchChange(e.target.checked)
                      }}
                      aria-label={t('settings.autoLaunch.label')}
                    />
                    <div
                      className={toggleSwitchClasses}
                      style={{
                        backgroundColor: autoLaunch ? primaryColor : undefined
                      }}
                    />
                  </label>
                </div>
                <p className={settingsMutedLineClass}>
                  {t('settings.autoLaunch.explanation')}
                </p>
              </div>
            )}

            {/* Windows System Controls - Only show on Windows when APIs are available */}
            {isSystemControlsAvailable() && (
              <div>
                <label className={formLabelClass}>
                  {t('settings.systemControls.title')}
                </label>

                {/* Show info message for Store installations */}
                {privilegeRestrictions?.isStoreInstall && (
                  <div className="mb-4 p-4 border border-blue-300 bg-blue-50 rounded-md">
                    <p className="text-sm text-blue-800">
                      {t('settings.systemControls.storeInstallWarning')}
                    </p>
                  </div>
                )}

                {/* Edge Swipe Toggle */}
                <div className="mb-3 p-3 border border-gray-200 rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">
                      {t('settings.systemControls.edgeSwipe.title')}
                    </span>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={isEdgeSwipeEnabled}
                        disabled={sysControlsLoading.edgeSwipe}
                        onChange={(e) => handleEdgeSwipeToggle(e.target.checked)}
                        aria-label={t('settings.systemControls.edgeSwipe.title')}
                      />
                      <div
                        className={toggleSwitchClasses}
                        style={{
                          backgroundColor: isEdgeSwipeEnabled ? primaryColor : undefined,
                          opacity: sysControlsLoading.edgeSwipe ? 0.5 : 1
                        }}
                      />
                    </label>
                  </div>
                  <p className="text-xs text-gray-500">
                    {t('settings.systemControls.edgeSwipe.explanation')}
                  </p>
                </div>

                {/* Lock on Wake Buttons */}
                <div className="mb-3 p-3 border border-gray-200 rounded-md">
                  <div className="mb-2">
                    <span className="text-sm font-medium">
                      {t('settings.systemControls.lockOnWake.title')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    {t('settings.systemControls.lockOnWake.explanation')}
                  </p>
                  <div className="flex justify-end gap-2">
                    <Button
                      text={t('settings.systemControls.lockOnWake.enable')}
                      onClick={handleLockOnWakeEnable}
                      disabled={
                        sysControlsLoading.lockOnWakeDisable || sysControlsLoading.lockOnWakeEnable
                      }
                      className="!px-4 !py-2 !text-sm"
                      variant="secondary"
                    />
                    <Button
                      text={t('settings.systemControls.lockOnWake.disable')}
                      onClick={handleLockOnWakeDisable}
                      disabled={
                        sysControlsLoading.lockOnWakeDisable || sysControlsLoading.lockOnWakeEnable
                      }
                      className="!px-4 !py-2 !text-sm"
                      style={{ backgroundColor: primaryColor }}
                    />
                  </div>
                  {(sysControlsLoading.lockOnWakeDisable ||
                    sysControlsLoading.lockOnWakeEnable) && (
                      <p className="text-xs text-gray-500 mt-2">
                        {t('settings.systemControls.applyingChanges')}
                      </p>
                    )}
                </div>

                {/* Power Timeouts Button */}
                <div className="mb-3 p-3 border border-gray-200 rounded-md">
                  <div className="mb-2">
                    <span className="text-sm font-medium">
                      {t('settings.systemControls.timeouts.title')}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mb-3">
                    {t('settings.systemControls.timeouts.explanation')}
                  </p>
                  <div className="flex justify-end">
                    <Button
                      text={t('settings.systemControls.timeouts.enable')}
                      onClick={handleTimeoutsEnable}
                      disabled={sysControlsLoading.timeouts}
                      className="!px-4 !py-2 !text-sm"
                      style={{ backgroundColor: primaryColor }}
                    />
                  </div>
                  {sysControlsLoading.timeouts && (
                    <p className="text-xs text-gray-500 mt-2">
                      {t('settings.systemControls.applyingChanges')}
                    </p>
                  )}
                </div>

                <p className="mt-1 text-xs text-gray-500 italic">
                  {t('settings.systemControls.description')}
                </p>
              </div>
            )}

            {/* Offline Mode Toggle */}
            {/* <div>
              <label className={formLabelClass}>
                {t('settings.offlineMode.title', 'Offline Mode')}
              </label>
              <div className={settingsInsetCardClass}>
                <div className="flex items-center gap-2">
                  <svg
                    className="w-5 h-5 text-gray-500"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
                    />
                  </svg>
                  <div className="flex flex-col">
                    <span className={settingsPortraitCompact ? 'text-xs' : 'text-sm'}>
                      {t('settings.offlineMode.useLocal', 'Use Local Files')}
                    </span>
                    {checkCanGoOffline() && (
                      <span className="text-xs text-green-600">
                        {t('settings.offlineMode.available', 'Available for your subscription')}
                        {isSubscriptionCacheValid() && (
                          <span className="ml-1">
                            ({t('settings.offlineMode.cached', 'cached')})
                          </span>
                        )}
                      </span>
                    )}
                    {!checkCanGoOffline() && (
                      <span className="text-xs text-gray-500">
                        {!isOfflineModeSupported() ? (
                          <span className="text-red-500">
                            {t(
                              'settings.offlineMode.requiresVersion',
                              'Requires app version 1.0.32 or higher'
                            )}{' '}
                            {appVersion && `(Current: v${appVersion})`}
                          </span>
                        ) : (
                          t(
                            'settings.offlineMode.requiresSubscription',
                            'Requires Professional, Enterprise, or ProTrial subscription'
                          )
                        )}
                      </span>
                    )}
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    className="sr-only peer"
                    checked={isOfflineMode}
                    onChange={(e) => {
                      if (checkCanGoOffline()) {
                        setOfflineMode(e.target.checked)
                      } else {
                        const errorMsg = !isOfflineModeSupported()
                          ? t(
                              'settings.offlineMode.versionError',
                              'Offline mode requires app version 1.0.32 or higher'
                            )
                          : t(
                              'settings.offlineMode.notAllowed',
                              'Offline mode requires a valid subscription'
                            )
                        cogoToast.error(errorMsg)
                      }
                    }}
                    disabled={!checkCanGoOffline()}
                    aria-label={t('settings.offlineMode.toggle', 'Toggle offline mode')}
                  />
                  <div
                    className={`${toggleSwitchClasses} ${!checkCanGoOffline() ? 'opacity-50 cursor-not-allowed' : ''}`}
                    style={{
                      backgroundColor:
                        isOfflineMode && checkCanGoOffline() ? primaryColor : undefined
                    }}
                  />
                </label>
              </div>
              <p className={settingsMutedLineClass}>
                {t(
                  'settings.offlineMode.explanation',
                  'Switch between web server and local files. Useful when internet is unreliable.'
                )}
              </p>
            </div> */}
          </div>

          <div className={footerOuterClass}>
            {useCompactModal ? (
              settingsPortraitCompact ? (
                <>
                  <div className="grid w-full grid-cols-2 gap-2">
                    <Button
                      text={
                        <div className="flex items-center justify-center gap-1">
                          <FaChevronLeft size={10} className="shrink-0" />
                          <span className="truncate">{t('settings.buttons.seeAllProjects')}</span>
                        </div>
                      }
                      variant="secondary"
                      onClick={async () => {
                        await handleEjectCamera()
                        window.location.href = '/'
                        setDefaultProjectId(undefined)
                      }}
                      className={footerSecondaryBtnClass}
                    />
                    <Button
                      text={
                        <div className="flex items-center justify-center gap-1">
                          <span className="truncate">{t('settings.buttons.editProject')}</span>
                          <FaExternalLinkAlt size={10} className="shrink-0" />
                        </div>
                      }
                      variant="secondary"
                      onClick={() =>
                        void openExternalUrl(
                          `${isKlakKlikKluk ? 'https://admin.klakklikkluk.com' : process.env.VITE_API_URL}/dashboard/projects/${projectId}`
                        )
                      }
                      className={footerSecondaryBtnClass}
                    />
                  </div>
                  <Button
                    text={t('settings.buttons.continue')}
                    onClick={async (e) => {
                      if (!isEditCustomPath && localTempCamera) {
                        await handleCameraChange(localTempCamera, e.nativeEvent)
                      }
                      handleContinue()
                    }}
                    className={`${footerPrimaryBtnClass} ${!isEditCustomPath && !isPreviewAvailable
                      ? '!bg-gray-300 !text-gray-500 opacity-40 cursor-not-allowed'
                      : ''
                      }`}
                  />
                </>
              ) : (
                <>
                  <Button
                    text={
                      <div className="flex items-center justify-center gap-1">
                        <FaChevronLeft size={12} className="shrink-0" />
                        <span className="truncate">{t('settings.buttons.seeAllProjects')}</span>
                      </div>
                    }
                    variant="secondary"
                    onClick={async () => {
                      await handleEjectCamera()
                      window.location.href = '/'
                      setDefaultProjectId(undefined)
                    }}
                    className={footerSecondaryBtnClass}
                  />
                  <Button
                    text={
                      <div className="flex items-center justify-center gap-1">
                        <span className="truncate">{t('settings.buttons.editProject')}</span>
                        <FaExternalLinkAlt size={12} className="shrink-0" />
                      </div>
                    }
                    variant="secondary"
                    onClick={() =>
                      void openExternalUrl(
                        `${isKlakKlikKluk ? 'https://admin.klakklikkluk.com' : process.env.VITE_API_URL}/dashboard/projects/${projectId}`
                      )
                    }
                    className={footerSecondaryBtnClass}
                  />
                  <Button
                    text={t('settings.buttons.continue')}
                    onClick={async (e) => {
                      if (!isEditCustomPath && localTempCamera) {
                        await handleCameraChange(localTempCamera, e.nativeEvent)
                      }
                      handleContinue()
                    }}
                    className={`${footerPrimaryBtnLandscapeClass || footerPrimaryBtnClass} ${!isEditCustomPath && !isPreviewAvailable
                      ? '!bg-gray-300 !text-gray-500 opacity-40 cursor-not-allowed'
                      : ''
                      }`}
                  />
                </>
              )
            ) : (
              <>
                <Button
                  text={
                    <div className="flex items-center gap-2">
                      <FaChevronLeft size={14} />
                      {t('settings.buttons.seeAllProjects')}
                    </div>
                  }
                  variant="secondary"
                  onClick={async () => {
                    await handleEjectCamera()
                    window.location.href = '/'
                    setDefaultProjectId(undefined)
                  }}
                  className="!px-6 !py-2 !text-base"
                />
                <Button
                  text={
                    <div className="flex items-center gap-2">
                      {t('settings.buttons.editProject')}
                      <FaExternalLinkAlt size={14} />
                    </div>
                  }
                  variant="secondary"
                  onClick={() =>
                    void openExternalUrl(
                      `${isKlakKlikKluk ? 'https://admin.klakklikkluk.com' : process.env.VITE_API_URL}/dashboard/projects/${projectId}`
                    )
                  }
                  className="!px-6 !py-2 !text-base"
                />
                <Button
                  text={t('settings.buttons.continue')}
                  onClick={async (e) => {
                    if (!isEditCustomPath && localTempCamera) {
                      await handleCameraChange(localTempCamera, e.nativeEvent)
                    }
                    handleContinue()
                  }}
                  className={`!px-6 !py-2 !text-base ${!isEditCustomPath && !isPreviewAvailable
                    ? '!bg-gray-300 !text-gray-500 opacity-40 cursor-not-allowed'
                    : ''
                    }`}
                />
              </>
            )}
          </div>
          {localAppVersion && (
            <p
              className={
                useCompactModal
                  ? settingsPortraitCompact
                    ? 'shrink-0 px-4 py-1 text-center text-[10px] text-gray-400'
                    : 'shrink-0 px-3 py-1 text-center text-xs text-gray-400'
                  : 'absolute bottom-2 left-4 mt-2 text-sm text-gray-300'
              }
            >
              {t('settings.appVersion.label')} {localAppVersion}
            </p>
          )}
          {ENABLE_UPDATE_NOTIFICATIONS && showUpdateBubble && latestAppVersion && (
            <div
              className={`absolute z-[210] flex cursor-pointer items-center gap-2 rounded-full bg-blue-500 p-2 text-sm font-bold text-white shadow-lg animate-bounce ${useCompactModal ? 'right-4 top-16 sm:right-12 sm:top-24' : 'right-12 top-24'
                }`}
              onClick={() =>
                void openExternalUrl(`${process.env.VITE_API_URL}/dashboard/installation`)
              }
            >
              {t('settings.appVersion.updateAvailable')} {latestAppVersion}{' '}
              <FaExternalLinkAlt size={14} />
            </div>
          )}
        </div>
      )}

      <CameraGuideModal
        isOpen={showGuideModal}
        onClose={() => setShowGuideModal(false)}
        isElectron={hasElectronPreload()}
        onFetchDevices={fetchDevices}
      />

      <PrinterGuideModal
        isOpen={showPrinterGuideModal}
        onClose={() => setShowPrinterGuideModal(false)}
      />

      <PrinterSettingsModal
        isOpen={showPrinterSettings}
        onClose={() => setShowPrinterSettings(false)}
        isElectron={hasElectronPreload()}
      />

      <RemotePrintSettingsModal
        isOpen={showRemotePrintSettings}
        onClose={() => setShowRemotePrintSettings(false)}
      />

      <CameraSettingsModal
        isOpen={showCameraSettings}
        onClose={() => {
          setShowCameraSettings(false)
          setIsPreviewAvailable(true)
        }}
      />

      <PinModal
        isOpen={showChangePinModal}
        onClose={() => setShowChangePinModal(false)}
        onSubmit={handlePinChange}
        title={pinModalTitle}
        subtitle={pinModalSubtitle}
      />

      {showUpgradeModal && <UpgradeModal onClose={() => setShowUpgradeModal(false)} />}
    </div>
  )
}

export default SettingsModal
